import React, { useEffect, useState } from "react";
import { AllowedDomainsTable } from "../../components/tables/AllowedDomainsTable";
import {
  ButtonProps,
  EmptyState,
  ListX,
  LoadingIndicator,
} from "@surya-digital/leo-reactjs-material-ui";
import { useTranslation } from "react-i18next";
import { useAllowedDomainsStore } from "../../stores/Hooks";
import { Stack } from "@mui/material";
import { observer } from "mobx-react";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { Plus } from "lucide-react";
import { useSignInStore } from "../../../store/Hooks";
import { UserPrivileges } from "../../../store/user/UserPrivileges";
import { Instance } from "mobx-state-tree";
import { AllowedDomainModel } from "../../models/AllowedDomainModel";

interface AllowedDomainsPageContentProps {
  onClickAddDomainButton: VoidFunction;
  onEditAllowedDomain: (
    allowedDomain: Instance<typeof AllowedDomainModel>,
  ) => void;
  onDeleteAllowedDomain: (
    allowedDomain: Instance<typeof AllowedDomainModel>,
  ) => void;
}

export const AllowedDomainsPageContent: React.FC<AllowedDomainsPageContentProps> =
  observer(
    ({
      onClickAddDomainButton,
      onEditAllowedDomain,
      onDeleteAllowedDomain,
    }) => {
      const [openUnhandledErrorDialog, setOpenUnhandledErrorDialog] =
        useState(false);

      const { t } = useTranslation();
      const translate = t as unknown as (key: string) => string;
      const allowedDomainsStore = useAllowedDomainsStore();
      const navigateTo = useRouteNavigator();
      const signInStore = useSignInStore();

      useEffect(() => {
        allowedDomainsStore
          .getAllowedDomains()
          .catch(() => setOpenUnhandledErrorDialog(true));
      }, []);

      if (openUnhandledErrorDialog) {
        return (
          <UnexpectedErrorDialog
            message={translate(
              "emailCycles.allowedDomainsGetAllowedDomainsUnexpectedErrorMessage",
            )}
            onClickDone={() => {
              setOpenUnhandledErrorDialog(false);
              navigateTo.project();
            }}
          />
        );
      }

      if (allowedDomainsStore.loadingAllowedDomains) {
        return (
          <LoadingIndicator
            variant="container"
            loadingText={t("common.searchTableLoadingState")}
            isLoading
          />
        );
      }

      const emptyStateButtonProps: ButtonProps | undefined =
        signInStore.isPrivilegeGranted(UserPrivileges.processAllowedDomains)
          ? {
              name: "addAllowedDomain",
              label: translate("emailCycles.allowedDomainsAddDomain"),
              variant: "outlined-color",
              size: "small",
              icon: <Plus />,
              onClick: onClickAddDomainButton,
            }
          : undefined;

      if (!allowedDomainsStore.allowedDomains.length) {
        return (
          <Stack justifyContent="center" height="100%">
            <EmptyState
              icon={<ListX />}
              title={translate("emailCycles.allowedDomainsEmptyStateTitle")}
              description={translate(
                "emailCycles.allowedDomainsEmptyStateDescription",
              )}
              width="100%"
              buttonProps={emptyStateButtonProps}
            />
          </Stack>
        );
      }

      return (
        <AllowedDomainsTable
          onEditAllowedDomain={onEditAllowedDomain}
          onDeleteAllowedDomain={onDeleteAllowedDomain}
        />
      );
    },
  );
